'use client';

import { type ReactElement } from 'react';

import { Header } from '@/components/SimplifiedSignup/styles';
import { useAnalytics } from '@/hooks/analytics';
import { useAmplitudeExperiment } from '@/hooks/utils/experiments/useAmplitudeExperiment';
import { useRouterPush } from '@/hooks/utils/useRouterPush';

import { useSisu, type ScreenName } from '../Provider';

export type HeaderProps = {
	/**
	 * @defaultValue
	 * ```tsx
	 * false
	 * ```
	 */
	hideExitButton?: boolean;

	/**
	 * @defaultValue
	 * ```tsx
	 * null
	 * ```
	 */
	logo?: ReactElement | null;

	onExit?: (activeScreen: ScreenName) => void;

	/**
	 * @defaultValue
	 * ```tsx
	 * "/app"
	 * ```
	 */
	routerPushPathname?: string;
};

const defaultProps = {
	hideForBusinessCta: true,
	isStatic: true,
	hideProfile: true,
	hideHambugerNav: true,
	hideAuthCTA: true,
	hideFreeTrialCta: true,
};

const getEventName = (activeScreen: ScreenName) => {
	if (activeScreen === 'account') {
		return 'Login Form : Exited';
	} else if (activeScreen === 'payment') {
		return 'Subscribe : Purchase : Form : Exited';
	}
	return `${activeScreen} : Exited`;
};

export default function SisuHeader({
	hideExitButton = false,
	logo = null,
	onExit,
	routerPushPathname = '/app',
}: HeaderProps) {
	const { activeScreen } = useSisu();

	const { logEvent } = useAnalytics();

	const { isEnrolled } = useAmplitudeExperiment('www-signup-flow-exit-account-screen');
	const hideExitIconInAccountScreen = isEnrolled && activeScreen === 'account';

	const routerPush = useRouterPush();

	const handleExit = () => {
		if (onExit) {
			onExit(activeScreen);
		} else {
			const eventName = getEventName(activeScreen);
			logEvent({ eventName });
			routerPush(routerPushPathname);
		}
	};

	return (
		<Header
			{...defaultProps}
			logo={logo}
			onExit={!hideExitButton && !hideExitIconInAccountScreen ? handleExit : undefined}
		/>
	);
}
